/**
 * Parametrizacao de modalidades (P-PAR-1): grupos do ramo, a modalidade aberta na
 * pagina "Editar configuracao" e a configuracao versionada; e os textos, as
 * clausulas, as coberturas, as complementares e os documentos exigidos (P-PAR-2).
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { api, requestPage } from '../client';
import { query, type ListParams, type Money } from './comum';
import type { BranchKind, Modality, ModalityGroup, Product } from './products';

export interface ModalityGroupInput {
  /** O ramo. So vale na criacao. */
  product_id?: string;
  name?: string;
  credit_group?: string;
  status?: string;
  default_economic_group_limit?: Money | null;
  ignore_branch_for_individual_insured?: boolean;
  ignore_branch_for_private_insured?: boolean;
  external_product_code?: string;
  /** Descricao do grupo (TOM-645). Vazia limpa. */
  description?: string;
}

/** Tratativa de inicio de vigencia do OnPoint. */
export type RetroactivityHandling = 'automatico' | 'esteira' | 'hibrido';

/**
 * Os parametros da modalidade, na ordem da aba "Detalhes da modalidade". Nulo e
 * "nao informado". Dinheiro como Money, taxa em pontos-base e prazos em dias.
 */
export interface ModalitySettingValues {
  max_insured_amount: Money | null;
  max_auto_approved_amount: Money | null;
  min_premium: Money | null;
  min_term_days: number | null;
  max_term_days: number | null;
  max_future_days: number | null;
  retroactivity_handling: RetroactivityHandling | null;
  max_retroactive_days: number | null;
  auto_issuance_enabled: boolean;
  is_complementary: boolean;
  allows_complementary: boolean;
  restricted_for_restricted_activity: boolean;
  equalize_coverage_terms: boolean;
  allows_individual_insured: boolean;
  allows_beneficiary: boolean;
  is_monitored: boolean;
  allows_step_in: boolean;
  is_large_risk: boolean;
  auto_cancel_expired: boolean;
  modality_rate_enabled: boolean;
  ignore_branch_for_individual_insured: boolean;
  auto_cancel_days: number | null;
  manual_cancel_days: number | null;
  modality_rate_bps: number | null;
  accumulation_enabled: boolean;
  retroactive_pipe_enabled: boolean;
  is_auctioneer: boolean;
}

export type VersionStatus = 'rascunho' | 'ativa' | 'revogada';

export interface ModalitySetting {
  id: string;
  modality_id: string;
  version: number;
  status: VersionStatus;
  based_on_version: number | null;
  notes: string;
  values: ModalitySettingValues;
  /** Quem pede criou ou editou a versao, e por isso nao a ativa. */
  authored_by_you: boolean;
  /**
   * Quem pede e autor e foi liberado nominalmente da segregacao (ADR 0091): pode
   * ativar mesmo assim, como autoaprovacao. So vem verdadeiro junto com a autoria.
   */
  may_self_approve?: boolean;
  created_label: string;
  updated_label: string;
  activated_label: string;
  activated_at: string | null;
  justification: string;
  effective_from: string | null;
  revoked_label: string;
  revoked_at: string | null;
  revocation_reason: string;
  created_at: string;
  updated_at: string;
}

export interface ModalityProductSummary {
  id: string;
  code: string;
  name: string;
  status: string;
  susep_branch_code: string | null;
  susep_group_code: string | null;
  susep_process_number: string | null;
  branch_kind: BranchKind | null;
}

export interface ModalityDetail extends Modality {
  product: ModalityProductSummary;
  modality_group: ModalityGroup | null;
  active_setting: ModalitySetting | null;
  draft_setting: ModalitySetting | null;
}

export interface ModalityListItem extends Modality {
  active_setting: ModalitySetting | null;
}

/** Edicao do cadastro da modalidade pela parametrizacao; o ausente fica como esta. */
export interface ModalityPatch {
  code?: string;
  name?: string;
  description?: string;
  status?: string;
  base_rate_bps?: number;
  credit_group?: string;
  credit_reference?: string;
  modality_group_id?: string;
  insurer_reference_code?: string;
  susep_process_number?: string;
}

export interface ModalityListParams extends ListParams {
  product_id?: string;
  modality_group_id?: string;
  ungrouped?: boolean;
  status?: string;
  sort?: string;
}

export const parametrization = {
  listGroups: (
    params: { product_id?: string; status?: string; sort?: string } = {},
    signal?: AbortSignal,
  ) => api.get<ModalityGroup[]>(`/api/v1/modality-groups${query(params)}`, signal),
  getGroup: (groupId: string, signal?: AbortSignal) =>
    api.get<ModalityGroup>(`/api/v1/modality-groups/${groupId}`, signal),
  createGroup: (input: ModalityGroupInput) =>
    api.post<ModalityGroup>('/api/v1/modality-groups', input),
  updateGroup: (groupId: string, input: ModalityGroupInput) =>
    api.patch<ModalityGroup>(`/api/v1/modality-groups/${groupId}`, input),
  removeGroup: (groupId: string) =>
    api.delete<void>(`/api/v1/modality-groups/${groupId}`),
  groupModalities: (groupId: string, signal?: AbortSignal) =>
    api.get<Modality[]>(`/api/v1/modality-groups/${groupId}/modalities`, signal),

  listModalities: (params: ModalityListParams = {}, signal?: AbortSignal) =>
    requestPage<ModalityListItem>(`/api/v1/modalities${query(params)}`, signal),
  getModality: (modalityId: string, signal?: AbortSignal) =>
    api.get<ModalityDetail>(`/api/v1/modalities/${modalityId}`, signal),
  updateModality: (modalityId: string, input: ModalityPatch) =>
    api.patch<ModalityDetail>(`/api/v1/modalities/${modalityId}`, input),

  listSettings: (modalityId: string, signal?: AbortSignal) =>
    api.get<ModalitySetting[]>(`/api/v1/modalities/${modalityId}/settings`, signal),
  createSetting: (
    modalityId: string,
    input: { notes?: string; values?: Partial<ModalitySettingValues> },
  ) => api.post<ModalitySetting>(`/api/v1/modalities/${modalityId}/settings`, input),
  updateSetting: (
    modalityId: string,
    settingId: string,
    input: { notes?: string; values?: Partial<ModalitySettingValues> },
  ) =>
    api.patch<ModalitySetting>(
      `/api/v1/modalities/${modalityId}/settings/${settingId}`,
      input,
    ),
  activateSetting: (modalityId: string, settingId: string, justification: string) =>
    api.post<ModalitySetting>(
      `/api/v1/modalities/${modalityId}/settings/${settingId}/activation`,
      { justification },
    ),

  // Carga com aprovacao (P-CARGA): o rascunho entra em vigor pela ativacao de outra
  // pessoa, na ordem ramo, grupo e modalidade.
  activateProduct: (productId: string, justification: string) =>
    api.post<Product>(`/api/v1/products/${productId}/activation`, { justification }),
  activateGroup: (groupId: string, justification: string) =>
    api.post<ModalityGroup>(`/api/v1/modality-groups/${groupId}/activation`, {
      justification,
    }),
  activateModality: (modalityId: string, justification: string) =>
    api.post<ModalityDetail>(`/api/v1/modalities/${modalityId}/activation`, {
      justification,
    }),
};

// --- Textos, clausulas, coberturas, complementares e documentos (P-PAR-2) ---------

/** Tipo da condicao: as abas Condicao Geral, Condicoes Especiais e Criterios. */
export type ModalityConditionKind =
  'condicao_geral' | 'condicoes_especiais' | 'criterios_enquadramento';

/** Finalidade do objeto: o da cotacao ou o impresso na apolice (so na cobertura). */
export type ModalityObjectPurpose = 'cotacao' | 'impressao_apolice';

/** "Selecione o tipo" da TAG. */
export type ModalityTermTagKind = 'texto' | 'editor_esteira' | 'data' | 'automatica';

export interface ModalityTermTag {
  label: string;
  /** A marcacao da plataforma, sem as chaves: objeto.numero_contrato. */
  tag_key: string;
  input_kind: ModalityTermTagKind;
}

/** Qual texto: o objeto ou uma condicao, da modalidade ou de uma cobertura. */
export type ModalityTermRef =
  | {
      family: 'objeto';
      modalityId: string;
      coverageId?: string | undefined;
      purpose?: ModalityObjectPurpose | undefined;
    }
  | {
      family: 'condicao';
      modalityId: string;
      coverageId?: string | undefined;
      kind: ModalityConditionKind;
    };

export interface ModalityTermVersion {
  id: string;
  modality_id: string;
  additional_coverage_id: string | null;
  family: 'objeto' | 'condicao';
  kind: string;
  purpose: ModalityObjectPurpose | null;
  version: number;
  status: VersionStatus;
  based_on_version: number | null;
  notes: string;
  insurer_reference: string | null;
  /** HTML saneado no servidor. */
  body: string;
  characters: number;
  enabled_on_quote: boolean;
  position: number;
  tags: ModalityTermTag[];
  authored_by_you: boolean;
  /**
   * Quem pede e autor e foi liberado nominalmente da segregacao (ADR 0091): pode
   * ativar mesmo assim, como autoaprovacao. So vem verdadeiro junto com a autoria.
   */
  may_self_approve?: boolean;
  created_label: string;
  updated_label: string;
  activated_label: string;
  activated_at: string | null;
  justification: string;
  effective_from: string | null;
  revoked_label: string;
  revoked_at: string | null;
  revocation_reason: string;
  created_at: string;
  updated_at: string;
}

export interface ModalityTermState {
  modality_id: string;
  additional_coverage_id: string | null;
  family: 'objeto' | 'condicao';
  kind: string;
  purpose: ModalityObjectPurpose | null;
  max_characters: number;
  active: ModalityTermVersion | null;
  draft: ModalityTermVersion | null;
}

/** "Salvar": o campo ausente fica como esta. */
export interface ModalityTermSaveInput {
  notes?: string;
  insurer_reference?: string;
  body?: string;
  enabled_on_quote?: boolean;
  position?: number;
  tags?: ModalityTermTag[];
}

export interface ParticularClause {
  id: string;
  modality_id: string;
  name: string;
  body: string;
  characters: number;
  status: string;
  broker_can_select: boolean;
  automatic_on_underwriting: boolean;
  enabled_on_quote: boolean;
  aggregator_clause: string | null;
  free_text: boolean;
  policyholder_id: string | null;
  version: number;
  tags: ModalityTermTag[];
  max_characters: number;
  created_at: string;
  updated_at: string;
}

export interface ParticularClauseInput {
  name?: string;
  body?: string;
  status?: string;
  broker_can_select?: boolean;
  automatic_on_underwriting?: boolean;
  enabled_on_quote?: boolean;
  aggregator_clause?: string;
  free_text?: boolean;
  policyholder_id?: string | null;
  tags?: ModalityTermTag[];
}

export type AdditionalCoverageCalculationType =
  'agravamento_premio' | 'distribuicao_premio';

export type AdditionalCoverageInsuredAmountBasis =
  'igual_basica' | 'valor_fixo' | 'percentual_basica';

export interface AdditionalCoverage {
  id: string;
  modality_id: string;
  name: string;
  insurer_reference_code: string | null;
  status: string;
  is_additional: boolean;
  auto_issuance_enabled: boolean;
  calculation_type: AdditionalCoverageCalculationType | null;
  aggravation_rate_bps: number | null;
  distribution_rate_bps: number | null;
  calculation_params: Record<string, string | number | boolean | null>;
  insured_amount_basis: AdditionalCoverageInsuredAmountBasis | null;
  insured_amount: Money | null;
  insured_amount_bps: number | null;
  allow_manual_edit: boolean;
  /** Sempre falso: nenhum calculo le a cobertura ate a homologacao. */
  calculation_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdditionalCoverageInput {
  name?: string;
  insurer_reference_code?: string;
  status?: string;
  is_additional?: boolean;
  auto_issuance_enabled?: boolean;
  calculation_type?: AdditionalCoverageCalculationType | null;
  aggravation_rate_bps?: number | null;
  distribution_rate_bps?: number | null;
  insured_amount_basis?: AdditionalCoverageInsuredAmountBasis | null;
  insured_amount?: Money | null;
  insured_amount_bps?: number | null;
  allow_manual_edit?: boolean;
}

export interface ComplementaryModalityLink {
  id: string;
  modality_id: string;
  complementary_modality_id: string;
  status: string;
  complementary: {
    name: string;
    code: string;
    status: string;
    product_id: string;
    product_name: string;
  };
  created_at: string;
  updated_at: string;
}

export interface RequiredDocument {
  id: string;
  scope: 'modalidade' | 'solicitacao_limite';
  modality_id: string | null;
  name: string;
  description: string;
  is_required: boolean;
  status: string;
  /** As esteiras, guardadas como vieram ate o pacote das esteiras. */
  pipe_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface RequiredDocumentInput {
  name?: string;
  description?: string;
  is_required?: boolean;
  status?: string;
  pipe_ids?: string[];
}

export interface ModalityTermsListParams extends ListParams {
  include_inactive?: boolean;
  sort?: string;
}

/** Caminho do texto: a modalidade, a cobertura e o tipo. */
function caminhoDoTexto(ref: ModalityTermRef): string {
  const base = `/api/v1/modalities/${ref.modalityId}${
    ref.coverageId ? `/additional-coverages/${ref.coverageId}` : ''
  }`;
  return ref.family === 'objeto' ? `${base}/object` : `${base}/conditions/${ref.kind}`;
}

/** A finalidade do objeto vai na consulta; a da cotacao e o padrao. */
function finalidadeDoTexto(ref: ModalityTermRef): string {
  return ref.family === 'objeto' && ref.purpose && ref.purpose !== 'cotacao'
    ? `?purpose=${ref.purpose}`
    : '';
}

export const modalityTerms = {
  getTerm: (ref: ModalityTermRef, signal?: AbortSignal) =>
    api.get<ModalityTermState>(
      `${caminhoDoTexto(ref)}${finalidadeDoTexto(ref)}`,
      signal,
    ),
  saveTerm: (ref: ModalityTermRef, input: ModalityTermSaveInput) =>
    api.put<ModalityTermVersion>(
      `${caminhoDoTexto(ref)}${finalidadeDoTexto(ref)}`,
      input,
    ),
  listTermVersions: (ref: ModalityTermRef, signal?: AbortSignal) =>
    api.get<ModalityTermVersion[]>(
      `${caminhoDoTexto(ref)}/versions${finalidadeDoTexto(ref)}`,
      signal,
    ),
  activateTerm: (ref: ModalityTermRef, versionId: string, justification: string) =>
    api.post<ModalityTermVersion>(
      `${caminhoDoTexto(ref)}/versions/${versionId}/activation${finalidadeDoTexto(ref)}`,
      { justification },
    ),

  listClauses: (
    modalityId: string,
    params: ModalityTermsListParams = {},
    signal?: AbortSignal,
  ) =>
    api.get<ParticularClause[]>(
      `/api/v1/modalities/${modalityId}/particular-clauses${query(params)}`,
      signal,
    ),
  createClause: (modalityId: string, input: ParticularClauseInput) =>
    api.post<ParticularClause>(
      `/api/v1/modalities/${modalityId}/particular-clauses`,
      input,
    ),
  updateClause: (modalityId: string, clauseId: string, input: ParticularClauseInput) =>
    api.patch<ParticularClause>(
      `/api/v1/modalities/${modalityId}/particular-clauses/${clauseId}`,
      input,
    ),

  listCoverages: (
    modalityId: string,
    params: ModalityTermsListParams = {},
    signal?: AbortSignal,
  ) =>
    api.get<AdditionalCoverage[]>(
      `/api/v1/modalities/${modalityId}/additional-coverages${query(params)}`,
      signal,
    ),
  createCoverage: (modalityId: string, input: AdditionalCoverageInput) =>
    api.post<AdditionalCoverage>(
      `/api/v1/modalities/${modalityId}/additional-coverages`,
      input,
    ),
  updateCoverage: (
    modalityId: string,
    coverageId: string,
    input: AdditionalCoverageInput,
  ) =>
    api.patch<AdditionalCoverage>(
      `/api/v1/modalities/${modalityId}/additional-coverages/${coverageId}`,
      input,
    ),

  listComplementary: (
    modalityId: string,
    params: ModalityTermsListParams = {},
    signal?: AbortSignal,
  ) =>
    api.get<ComplementaryModalityLink[]>(
      `/api/v1/modalities/${modalityId}/complementary-modalities${query(params)}`,
      signal,
    ),
  createComplementary: (modalityId: string, complementaryModalityId: string) =>
    api.post<ComplementaryModalityLink>(
      `/api/v1/modalities/${modalityId}/complementary-modalities`,
      { complementary_modality_id: complementaryModalityId },
    ),
  updateComplementary: (modalityId: string, linkId: string, status: string) =>
    api.patch<ComplementaryModalityLink>(
      `/api/v1/modalities/${modalityId}/complementary-modalities/${linkId}`,
      { status },
    ),

  listRequiredDocuments: (
    modalityId: string,
    params: ModalityTermsListParams = {},
    signal?: AbortSignal,
  ) =>
    api.get<RequiredDocument[]>(
      `/api/v1/modalities/${modalityId}/required-documents${query(params)}`,
      signal,
    ),
  createRequiredDocument: (modalityId: string, input: RequiredDocumentInput) =>
    api.post<RequiredDocument>(
      `/api/v1/modalities/${modalityId}/required-documents`,
      input,
    ),
  updateRequiredDocument: (
    modalityId: string,
    documentId: string,
    input: RequiredDocumentInput,
  ) =>
    api.patch<RequiredDocument>(
      `/api/v1/modalities/${modalityId}/required-documents/${documentId}`,
      input,
    ),
  removeRequiredDocument: (modalityId: string, documentId: string) =>
    api.delete<void>(
      `/api/v1/modalities/${modalityId}/required-documents/${documentId}`,
    ),

  listLimitDocuments: (params: ModalityTermsListParams = {}, signal?: AbortSignal) =>
    api.get<RequiredDocument[]>(
      `/api/v1/settings/limit-request-documents${query(params)}`,
      signal,
    ),
  createLimitDocument: (input: RequiredDocumentInput) =>
    api.post<RequiredDocument>('/api/v1/settings/limit-request-documents', input),
  updateLimitDocument: (documentId: string, input: RequiredDocumentInput) =>
    api.patch<RequiredDocument>(
      `/api/v1/settings/limit-request-documents/${documentId}`,
      input,
    ),
};

// --- Nome da cobertura impresso na apolice (P-MIN-2) -----------------------------

export const policyCoverageName = {
  get: (productId: string, modalityId: string, signal?: AbortSignal) =>
    api.get<{ policy_coverage_name: string }>(
      `/api/v1/products/${productId}/modalities/${modalityId}/policy-coverage-name`,
      signal,
    ),
  save: (productId: string, modalityId: string, name: string) =>
    api.put<{ policy_coverage_name: string }>(
      `/api/v1/products/${productId}/modalities/${modalityId}/policy-coverage-name`,
      { policy_coverage_name: name },
    ),
};
